import { Gift, Award, Sparkles, Settings2 } from 'lucide-react';
import type { GrantTipo } from '@/types/subscription.types';
import type { PlanoCard, VigenciaAnos } from './types';

// Catálogo dos cards do protótipo (seção "① Plano"). NÃO é 1:1 com a tabela
// `planos` do backend — 'trial' e 'enterprise' são pseudo-planos (ver
// adminSubscriptionService.js PRESET_TIPOS): 'trial' vira o GrantTipo
// `trial_15d` direto; 'enterprise' abre o modo custom (não tem GrantTipo).
export const PLANO_CARDS: {
  card: PlanoCard;
  label: string;
  icon: typeof Gift;
  // codigo do catálogo `planos` (basico/profissional) — null p/ pseudo-planos.
  codigo: 'basico' | 'profissional' | null;
}[] = [
  // Sem número de dias fixo — quem exibe (PlanCards/SummaryBanner) usa
  // useTrialDays() pra puxar o valor real de `system_settings` via API.
  { card: 'trial', label: 'Teste grátis', icon: Gift, codigo: null },
  { card: 'basico', label: 'Básico', icon: Award, codigo: 'basico' },
  { card: 'profissional', label: 'Profissional', icon: Sparkles, codigo: 'profissional' },
  { card: 'enterprise', label: 'Enterprise', icon: Settings2, codigo: null },
];

// Mapeia (plano básico/profissional + ciclo) -> GrantTipo padrão (1 clique).
// Tipado com o subconjunto SEM 'custom' pra o TS discriminar a união
// GrantSubscriptionInput (padrão não exige `status`; custom exige).
export const GRANT_TIPO_PADRAO: Record<
  'basico' | 'profissional',
  Record<'mensal' | 'anual', Exclude<GrantTipo, 'custom'>>
> = {
  basico: { mensal: 'basico_mensal', anual: 'basico_anual' },
  profissional: { mensal: 'profissional_mensal', anual: 'profissional_anual' },
};

export const VIGENCIA_OPTIONS: { anos: VigenciaAnos; label: string }[] = [
  { anos: 1, label: '1 ano' },
  { anos: 2, label: '2 anos' },
  { anos: 3, label: '3 anos' },
];

// Soma meses preservando "fim de mês" — espelha addMonths do adminSubscriptionService.js
// (mesma regra, pro banner de resumo bater com o que o backend vai calcular).
export function addMonths(date: Date, months: number): Date {
  const d = new Date(date.getTime());
  const targetMonth = d.getMonth() + months;
  const expectedMonth = ((targetMonth % 12) + 12) % 12;
  d.setMonth(targetMonth);
  if (d.getMonth() !== expectedMonth) d.setDate(0);
  return d;
}

// current_period_end previsto p/ o contrato Enterprise (hoje + N anos).
export function computeVigenciaEnd(anos: VigenciaAnos, from: Date = new Date()): Date {
  return addMonths(from, anos * 12);
}

// current_period_end de um plano padrão PAGO (não-cortesia): hoje + 1 mês / 1 ano.
export function computePeriodEndByCiclo(ciclo: 'mensal' | 'anual', from: Date = new Date()): Date {
  return addMonths(from, ciclo === 'anual' ? 12 : 1);
}

// Preço mensal em reais a partir do plano (centavos) + ciclo, já dividido por mês
// (o preço anual do catálogo é o total do ano, não a parcela).
export function precoMensalReais(
  precoMensalCentavos: number | null,
  precoAnualCentavos: number | null,
  ciclo: 'mensal' | 'anual'
): number | null {
  if (ciclo === 'anual') {
    if (precoAnualCentavos == null) return null;
    return precoAnualCentavos / 12 / 100;
  }
  if (precoMensalCentavos == null) return null;
  return precoMensalCentavos / 100;
}

export const emptyLimites = {
  animais: '',
  funcionarios: '',
  propriedades: '',
};
